import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { listAllSessions } from '../../service/SessionService';
import Session from '../../models/Session';
import { PageResponse } from '../../models/PageResponse';
import clsx from "clsx";

type SortState = { field: string; dir: "asc" | "desc" };

const ScanningSession = () => {

    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [sort, setSort] = useState<SortState>({ field: "createdDate", dir: "desc" });

    const navigate = useNavigate();
    const [data, setData] = useState<PageResponse<Session> | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Build URL with query params
    const urlParams = useMemo<string>(() => {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("size", String(size));
        params.set("sort", `${sort.field},${sort.dir}`);
        return params.toString();
    }, [page, size, sort]);

    const toggleSort = (field: string) => {
        setPage(0); // reset page when sorting changes
        setSort((prev) =>
            prev.field === field ? { field, dir: prev.dir === "asc" ? "desc" : "asc" } : { field, dir: "asc" }
        );
    };

    // Derived helpers
    const from = data ? page * size + 1 : 0;
    const to = data ? page * size + data.content.length : 0;

    const goToDetail = (sessionId : string) => {
        navigate(`/dashboard/scanning-session/${sessionId}`);
    };

    const goToCreateNewSession = () => {
        navigate(`/dashboard/create-new-session`);
    };

    type ThProps = {
        children: React.ReactNode;
        onClick?: () => void;
        active?: boolean;
        dir?: "asc" | "desc";
        className?: string;
    };

    // Simple sortable header cell
    const Th: React.FC<ThProps> = ({ children, onClick, active, dir, className }) => {
        return (
            <th
            onClick={onClick}
            className={clsx(
                "cursor-pointer select-none",
                active && "text-blue-700 font-semibold",
                className
            )}
            >
            {children}
            {active && (dir === "asc" ? " ▲" : " ▼")}
            </th>
        );
    };

    const fetchSessions = async () => {
        setIsLoading(true);
        try {
            const sessions = await listAllSessions(urlParams);
            setData(sessions);
        } catch (error) {
            console.error('Failed to fetch scanning sessions:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSessions();
    }, [urlParams]);

    return (
        <section className="dashboard-section">
            <div className="bg-white rounded-2xl shadow-sm p-6">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-gray-800 mb-6">Sessions</h2>
                    <button
                        className="gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition"
                        onClick={goToCreateNewSession}
                    >
                        Create Session
                    </button>
                </div>
                {isLoading ? (
                    <div className="text-center">Loading...</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                            <thead>
                                <tr>
                                    <Th
                                        className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                                        onClick={() => toggleSort("createdDate")}
                                        active={sort.field === "createdDate"}
                                        dir={sort.dir}
                                    >
                                        Created Date
                                    </Th>
                                    <Th
                                        className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                                        onClick={() => toggleSort("title")}
                                        active={sort.field === "title"}
                                        dir={sort.dir}
                                    >
                                        Title
                                    </Th>
                                    <Th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                        Delivery Notes
                                    </Th>
                                    <Th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                        Status
                                    </Th>
                                </tr>

                            </thead>
                            <tbody>
                                {data?.content.map((session, index) => (
                                    <tr 
                                        key={session.id} 
                                        onClick={() => goToDetail(session.id)}
                                        //className="border-b"
                                        className={`cursor-pointer transition-colors duration-200 hover:bg-blue-50/60
                                            ${ index % 2 === 0 ? 'bg-white' : 'bg-gray-50' }`}
                                    >
                                        <td className="px-4 py-2">{session.createdDate}</td>
                                        <td className="px-4 py-2">{session.title}</td>
                                        <td className="px-4 py-2">{session.deliveryNotes?.join(', ')}</td>
                                        <td className="px-4 py-2">
                                            {session.posted ? (
                                                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600">
                                                    Posted
                                                </span>
                                            ) : session.closed ? (
                                                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                                                    Closed
                                                </span>
                                            ) : (
                                                <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
                                                    Open
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {data?.content.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="text-center py-4 text-gray-400"> {/* Updated colSpan to 6 */}
                                            No sessions found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>

                        {/* Pagination + Toolbar */}
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mt-6 px-2">
                            {/* Info */}
                            <div className="text-sm text-gray-600">
                                {data && data.totalElements > 0 && (
                                    <span>
                                        Showing <span className="font-medium">{from}</span>–<span className="font-medium">{to}</span> of{" "}
                                        <span className="font-medium">{data.totalElements}</span>
                                    </span>
                                )}
                            </div>

                            {/* Pagination controls */}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setPage(0)}
                                    disabled={!data || page === 0 || isLoading}
                                    className="px-3 py-1 rounded-lg border text-sm disabled:opacity-40 hover:bg-gray-100 transition"
                                >
                                    « First
                                </button>
                                <button
                                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                                    disabled={!data || page === 0 || isLoading}
                                    className="px-3 py-1 rounded-lg border text-sm disabled:opacity-40 hover:bg-gray-100 transition"
                                >
                                    ‹ Prev
                                </button>
                                <span className="text-sm text-gray-700">
                                    Page <span className="font-medium">{page + 1}</span>
                                    {data && ` / ${data.totalPages}`}
                                </span>
                                <button
                                    onClick={() => setPage((p) => (data ? Math.min(data.totalPages - 1, p + 1) : p))}
                                    disabled={!data || page >= (data?.totalPages ?? 1) - 1 || isLoading}
                                    className="px-3 py-1 rounded-lg border text-sm disabled:opacity-40 hover:bg-gray-100 transition"
                                >
                                    Next ›
                                </button>
                                <button
                                    onClick={() => data && setPage(data.totalPages - 1)}
                                    disabled={!data || page >= (data?.totalPages ?? 1) - 1 || isLoading}
                                    className="px-3 py-1 rounded-lg border text-sm disabled:opacity-40 hover:bg-gray-100 transition"
                                >
                                    Last »
                                </button>
                            </div>

                            {/* Toolbar */}
                            <div className="flex items-center gap-2">
                                <label className="text-sm text-gray-600">
                                    Page size:
                                    <select
                                        value={size}
                                        onChange={(e) => { setPage(0); setSize(Number(e.target.value)); }}
                                        className="ml-2 border rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    >
                                        {[10, 20, 50, 100].map((n) => (
                                            <option key={n} value={n}>
                                                {n}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                            </div>
                        </div>

                    </div>
                )}
            </div>
        </section>
    );
};

export default ScanningSession;